import React, { useEffect, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import axios from "axios";
import { useSelector } from "react-redux";
import { Box, Typography, CircularProgress, Card } from "@mui/material";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export default function GrowthChart({ modelName, type = "bar", title, color = "#6366f1" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const auth = useSelector((state) => state?.loginState?.auth);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_URL || 'http://127.0.0.1:8000/api/'}chart/0/${modelName}/`, {
          headers: { Authorization: `Bearer ${auth?.token}` }
        });
        
        const monthData = response.data?.data?.results?.month || response.data?.results?.month || [];
        
        // Map data to all 12 months
        const labels = MONTHS;
        const counts = Array(12).fill(0);
        
        monthData.forEach(item => {
          if (item.label >= 1 && item.label <= 12) {
            counts[item.label - 1] = item.dcount;
          }
        });

        setData({
          labels,
          datasets: [
            {
              label: title,
              data: counts,
              backgroundColor: type === "bar" ? `${color}88` : `${color}33`,
              borderColor: color,
              borderWidth: 2,
              borderRadius: type === "bar" ? 6 : 0,
              fill: type === "line",
              tension: 0,
              pointRadius: type === "line" ? 4 : 0,
              pointBackgroundColor: color,
            },
          ],
        });
      } catch (err) {
        console.error("Failed to fetch chart data for", modelName, err);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [modelName, auth?.token, title, color, type]);

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.9)",
        titleFont: { size: 13, family: "Inter" },
        bodyFont: { size: 14, weight: "bold", family: "Inter" },
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: { stepSize: 1, color: "#94a3b8" },
        grid: { color: "rgba(226, 232, 240, 0.5)", drawBorder: false },
        border: { display: false }
      },
      x: {
        ticks: { color: "#94a3b8" },
        grid: { display: false },
        border: { display: false }
      },
    },
  };

  return (
    <Card sx={{ p: 3, height: "100%", borderRadius: 3, boxShadow: "0 4px 20px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9", display: "flex", flexDirection: "column" }}>
      <Typography sx={{ fontWeight: 700, color: "#1e293b", mb: 0.5, fontSize: 16 }}>{title}</Typography>
      <Typography sx={{ color: "#94a3b8", fontSize: 12, mb: 3 }}>Monthly Creation Trends (This Year)</Typography>
      
      <Box sx={{ flexGrow: 1, minHeight: 250, position: "relative" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <CircularProgress size={24} sx={{ color }} />
          </Box>
        ) : data ? (
          type === "bar" ? (
            <Bar options={options} data={data} />
          ) : (
            <Line options={options} data={data} />
          )
        ) : (
          <Typography sx={{ textAlign: "center", mt: 5, color: "#94a3b8" }}>No data available</Typography>
        )}
      </Box>
    </Card>
  );
}
